import React, {memo, useCallback, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import './menuBar.scss';
import {DropdownMenuCompactRef} from '../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {selectedPresentationMetaSelector, selectedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {isSlideModifiedSelector, selectedSlideSelector} from '../../../../../store/selectors/slide.selectors';
import {useSlideUpdateContext} from '../../../../context/SetSlideUpdateContext';
import CreateRoomModal from '../../../../pages/rooms/createRoom/CreateRoomModal';
import CreatePresentationLinksModals from '../../../presentationList/presentationLinks/CreatePresentationLinksModals';
import PresentationListModals from '../../../presentationList/PresentationListModals';
import ExportToPdfModal from '../../modal/ExportToPdfModal';
import ImportSlideModal from '../../modal/importSlideModal/ImportSlideModal';
import MenuBarEditDropdown from './menus/MenuBarEdit';
import MenuBarFileDropdown from './menus/MenuBarFile';
import MenuBarInsertDropdown from './menus/MenuBarInsert';
import MenuBarReportsDropdown from './menus/MenuBarReports';
import MenuBarRunDropdown from './menus/MenuBarRun';
import MenuBarSlideDropdown from './menus/MenuBarSlide';
import MenuBarViewDropdown from './menus/MenuBarView';
import {useConnectionsPaginatedQuery, toPaginatedResultWrapper} from '../../../../../hooks/connectionQueries';
import {connectionListSearchTermSelector, connectionListPageSelector} from '../../../../../store/selectors/connection.selectors';

function MenuBar(): JSX.Element {
    const {setAndSaveSlide} = useSlideUpdateContext();

    const selectedSlide = useSelector(selectedSlideSelector);

    const selectedPresentationMeta = useSelector(selectedPresentationMetaSelector);
    const isSlideModified = useSelector(isSlideModifiedSelector);
    const connectionListSearchTerm = useSelector(connectionListSearchTermSelector);
    const connectionListPage = useSelector(connectionListPageSelector);
    const connectionsPaginatedSearch = useMemo(
        () => ({
            searchString: connectionListSearchTerm,
            pageNumber: connectionListPage,
            pageSize: 5,
        }),
        [connectionListSearchTerm, connectionListPage]
    );
    const connectionsQuery = useConnectionsPaginatedQuery(connectionsPaginatedSearch);
    const connections = toPaginatedResultWrapper(connectionsPaginatedSearch, connectionsQuery.data, connectionsQuery.isFetching);
    const selectedPresentation = useSelector(selectedPresentationSelector);

    const [createPresentationLinksModal, setCreatePresentationLinksModal] = useState(false);
    const [sendPresentationModal, setSendPresentationModal] = useState(false);
    const [exportToPdfModal, setExportToPdfModal] = useState(false);
    const [isCreateRoomModalOpen, setCreateRoomModalOpen] = useState<boolean>(false);
    const [menuBarOpen, setMenuBarOpen] = useState<boolean>(false);
    const [openImportModal, setImportModalOpen] = useState(false);

    const fileMenuItem = useRef<DropdownMenuCompactRef>(null);
    const editMenuItem = useRef<DropdownMenuCompactRef>(null);
    const viewMenuItem = useRef<DropdownMenuCompactRef>(null);
    const slideMenuItem = useRef<DropdownMenuCompactRef>(null);
    const insertMenuItem = useRef<DropdownMenuCompactRef>(null);
    const runMenuItem = useRef<DropdownMenuCompactRef>(null);
    const reportsMenuBar = useRef<DropdownMenuCompactRef>(null);

    const closeAllMenusExcept = useCallback((except?: React.RefObject<DropdownMenuCompactRef>): void => {
        const menus = [fileMenuItem, editMenuItem, viewMenuItem, slideMenuItem, insertMenuItem, runMenuItem, reportsMenuBar];
        menus.forEach((el) => {
            if (el !== except) el.current?.closeMenu();
        });
    }, []);

    const mutateOnModified = useCallback(
        (cb?: () => void): void => {
            if (isSlideModified) {
                setAndSaveSlide(selectedSlide, {onSuccess: cb});
            } else cb?.();
        },
        [isSlideModified, selectedSlide, setAndSaveSlide]
    );

    const closeMenuBar = useCallback(() => setMenuBarOpen(false), []);
    const openMenuBar = useCallback(() => setMenuBarOpen(true), []);

    const onHoverFile = useCallback(() => closeAllMenusExcept(fileMenuItem), [closeAllMenusExcept]);
    const onHoverEdit = useCallback(() => closeAllMenusExcept(editMenuItem), [closeAllMenusExcept]);
    const onHoverView = useCallback(() => closeAllMenusExcept(viewMenuItem), [closeAllMenusExcept]);
    const onHoverSlide = useCallback(() => closeAllMenusExcept(slideMenuItem), [closeAllMenusExcept]);
    const onHoverInsert = useCallback(() => closeAllMenusExcept(insertMenuItem), [closeAllMenusExcept]);
    const onHoverRun = useCallback(() => closeAllMenusExcept(runMenuItem), [closeAllMenusExcept]);
    const onHoverReports = useCallback(() => closeAllMenusExcept(reportsMenuBar), [closeAllMenusExcept]);

    const closeCreatePresentationLinksModal = useCallback(() => setCreatePresentationLinksModal(false), []);

    return (
        <>
            <div className="c-menuBar">
                <MenuBarFileDropdown
                    fileMenuItemRef={fileMenuItem}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverFile}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                    mutateOnModified={mutateOnModified}
                    onOpenExportPdfModal={() => setExportToPdfModal(true)}
                    onOpenCreatePresentationLinksModal={() => setCreatePresentationLinksModal(true)}
                    onOpenSendPresentationModal={() => setSendPresentationModal(true)}
                />
                <MenuBarEditDropdown
                    editMenuItemRef={editMenuItem}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverEdit}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                />
                <MenuBarViewDropdown
                    viewMenuItemRef={viewMenuItem}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverView}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                />
                <MenuBarSlideDropdown
                    slideMenuItemRef={slideMenuItem}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverSlide}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                    onOpenImportModal={() => setImportModalOpen(true)}
                />
                <MenuBarInsertDropdown
                    insertMenuItemRef={insertMenuItem}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverInsert}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                />
                <MenuBarRunDropdown
                    runMenuItemRef={runMenuItem}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverRun}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                    onOpenCreateRoomModal={() => setCreateRoomModalOpen(true)}
                />
                <MenuBarReportsDropdown
                    reportsMenuItemRef={reportsMenuBar}
                    menuBarOpen={menuBarOpen}
                    onHoverEnter={onHoverReports}
                    onMenuClose={closeMenuBar}
                    onOpenMenuBar={openMenuBar}
                    mutateOnModified={mutateOnModified}
                />
            </div>

            <ExportToPdfModal
                isOpen={exportToPdfModal}
                onCancel={() => setExportToPdfModal(false)}
                onConfirm={() => setExportToPdfModal(false)}
            />

            <CreatePresentationLinksModals
                key={selectedPresentationMeta?._id}
                presentation={selectedPresentationMeta}
                open={createPresentationLinksModal}
                closeModal={closeCreatePresentationLinksModal}
            />

            <PresentationListModals
                sendPresentationModal={sendPresentationModal}
                setSendPresentationModal={setSendPresentationModal}
                connections={connections}
                selected={[selectedPresentation]}
            />

            <ImportSlideModal
                isOpen={openImportModal}
                onCancel={() => {
                    setImportModalOpen(false);
                }}
                onConfirm={() => {
                    setImportModalOpen(false);
                }}
            />

            <CreateRoomModal open={isCreateRoomModalOpen} setOpen={setCreateRoomModalOpen} presentation={selectedPresentation} />
        </>
    );
}

export default memo(MenuBar);
